import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ChevronLeft, FileText, AlertTriangle } from 'lucide-react';

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-4 lg:px-8 h-16 flex items-center">
        <div className="max-w-4xl mx-auto w-full flex items-center gap-4">
          <Link to="/">
            <Button variant="ghost" className="text-slate-600">
              <ChevronLeft className="w-5 h-5 mr-1" />
              Back to Home
            </Button>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="py-12 px-4 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl border border-slate-200 p-8 lg:p-12 shadow-sm">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 bg-slate-900 rounded-xl flex items-center justify-center">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Terms of Service
                </h1>
                <p className="text-slate-500">Effective Date: February 2025</p>
              </div>
            </div>

            <div className="prose prose-slate max-w-none">
              {/* Important Notice */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-8 flex gap-3">
                <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0" />
                <div>
                  <p className="text-amber-800 font-semibold mb-1">Important</p>
                  <p className="text-amber-700 text-sm">
                    Please read these Terms of Service carefully before using Career Step. By creating an account or using our services, 
                    you agree to be legally bound by these terms. If you do not agree, do not use our services.
                  </p>
                </div>
              </div>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-slate-900 mb-4">1. Definitions</h2>
                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                  <li><strong>"Career Step"</strong>, <strong>"we"</strong>, <strong>"us"</strong>, or <strong>"our"</strong> refers to the Career Step platform and its operators.</li>
                  <li><strong>"Service"</strong> refers to the Career Step website, applications, and all related features.</li>
                  <li><strong>"User"</strong>, <strong>"you"</strong>, or <strong>"your"</strong> refers to any individual who accesses or uses the Service.</li>
                  <li><strong>"Subscriber"</strong> refers to a User with an active paid subscription.</li>
                  <li><strong>"CV"</strong> refers to any curriculum vitae or resume created using our Service.</li>
                  <li><strong>"Content"</strong> refers to any information, text, or data you submit to the Service.</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-slate-900 mb-4">2. Acceptance of Terms</h2>
                <p className="text-slate-600 mb-4">
                  By accessing or using Career Step, you confirm that:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                  <li>You are at least 16 years of age;</li>
                  <li>You have the legal capacity to enter into a binding agreement;</li>
                  <li>You agree to comply with these Terms and all applicable South African laws;</li>
                  <li>You have read and understood our <Link to="/privacy" className="text-amber-600 hover:underline">Privacy Policy</Link>.</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-slate-900 mb-4">3. Service Description</h2>
                <p className="text-slate-600 mb-4">
                  Career Step is a subscription-based platform that provides:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                  <li>Professional CV creation tools with multiple ATS-friendly templates;</li>
                  <li>AI-powered content enhancement and language improvement;</li>
                  <li>Job description matching and ATS compatibility scoring;</li>
                  <li>Interview preparation tools and mock interview features;</li>
                  <li>Salary information and negotiation guidance;</li>
                  <li>Career planning and workplace rights information;</li>
                  <li>Job search aggregation from multiple South African job boards;</li>
                  <li>PDF CV generation and download.</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-slate-900 mb-4">4. Subscription and Payment</h2>
                
                <div className="bg-slate-50 rounded-xl p-5 mb-4">
                  <h3 className="text-lg font-semibold text-slate-800 mb-3">4.1 Subscription Fee</h3>
                  <ul className="list-disc pl-6 text-slate-600 space-y-2">
                    <li>The subscription fee is <strong>R25.00 (twenty-five South African Rand) for 30 days of access</strong>.</li>
                    <li>All prices are inclusive of VAT where applicable.</li>
                    <li>We reserve the right to change subscription fees with 30 days' notice.</li>
                  </ul>
                </div>

                <div className="bg-slate-50 rounded-xl p-5 mb-4">
                  <h3 className="text-lg font-semibold text-slate-800 mb-3">4.2 Payment Processing</h3>
                  <ul className="list-disc pl-6 text-slate-600 space-y-2">
                    <li>Payments are processed securely through PayFast, a licensed South African payment gateway.</li>
                    <li>We do not store your credit card or banking details on our servers.</li>
                    <li>Payments are once-off. There is no automatic renewal - you choose when to pay again.</li>
                  </ul>
                </div>

                <div className="bg-slate-50 rounded-xl p-5 mb-4">
                  <h3 className="text-lg font-semibold text-slate-800 mb-3">4.3 Access Expiry</h3>
                  <ul className="list-disc pl-6 text-slate-600 space-y-2">
                    <li>After 30 days, your premium access will expire automatically.</li>
                    <li>To continue using premium features, simply make another R25 payment.</li>
                    <li>Your account and CVs are never deleted - you can renew access at any time.</li>
                    <li>No refunds are provided for unused portions of your access period.</li>
                  </ul>
                </div>

                <div className="bg-slate-50 rounded-xl p-5">
                  <h3 className="text-lg font-semibold text-slate-800 mb-3">4.4 Service Limits</h3>
                  <ul className="list-disc pl-6 text-slate-600 space-y-2">
                    <li>Each subscriber may create a maximum of <strong>6 (six) CVs</strong>.</li>
                    <li>One account per individual is permitted.</li>
                    <li>Account sharing is strictly prohibited.</li>
                  </ul>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-slate-900 mb-4">5. User Responsibilities and Conduct</h2>
                <p className="text-slate-600 mb-4">By using our Service, you agree to:</p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                  <li><strong>Provide accurate information:</strong> All information in your CV must be truthful and accurate. You are solely responsible for the content you create.</li>
                  <li><strong>Not falsify credentials:</strong> You must not create fake qualifications, fabricate work experience, invent skills, or misrepresent your background.</li>
                  <li><strong>Maintain account security:</strong> You are responsible for maintaining the confidentiality of your login credentials.</li>
                  <li><strong>Not share accounts:</strong> Your account is personal and non-transferable.</li>
                  <li><strong>Comply with laws:</strong> You must not use the Service for any unlawful purpose or to violate any applicable laws.</li>
                  <li><strong>Respect intellectual property:</strong> You must not copy, distribute, or create derivative works from our templates or platform.</li>
                  <li><strong>Not circumvent restrictions:</strong> You must not attempt to bypass subscription limits or access controls.</li>
                  <li><strong>Not abuse the Service:</strong> You must not use automated tools, bots, or scripts to access the Service.</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-slate-900 mb-4">6. AI Content Enhancement</h2>
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 mb-4">
                  <h3 className="text-lg font-semibold text-emerald-800 mb-2">Our Ethical AI Commitment</h3>
                  <p className="text-emerald-700">
                    Career Step's AI features are designed to <strong>enhance, restructure, and improve the language</strong> of content you provide. 
                    Our AI will <strong>NEVER</strong>:
                  </p>
                  <ul className="list-disc pl-6 text-emerald-700 space-y-1 mt-2">
                    <li>Invent or fabricate qualifications, degrees, or certifications;</li>
                    <li>Create fictional work experience or job titles;</li>
                    <li>Add skills or competencies you did not mention;</li>
                    <li>Exaggerate or embellish your achievements beyond what you provided.</li>
                  </ul>
                </div>
                <p className="text-slate-600 mb-4">
                  <strong>Your responsibility:</strong> You remain solely responsible for reviewing all AI-generated suggestions and ensuring the accuracy and truthfulness of your final CV. AI outputs are suggestions only and should be verified before use.
                </p>
                <p className="text-slate-600">
                  <strong>Disclaimer:</strong> While we design our AI to be helpful and accurate, we cannot guarantee that AI outputs will be error-free. You must review and approve all content before downloading or sharing your CV.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-slate-900 mb-4">7. Intellectual Property Rights</h2>
                <h3 className="text-lg font-semibold text-slate-800 mb-2">7.1 Your Content</h3>
                <p className="text-slate-600 mb-4">
                  You retain ownership of the personal information and content you submit to Career Step. By using our Service, you grant us a limited, non-exclusive licence to process, store, and display your content solely for the purpose of providing the Service.
                </p>
                
                <h3 className="text-lg font-semibold text-slate-800 mb-2">7.2 Our Property</h3>
                <p className="text-slate-600 mb-4">
                  Career Step, including its design, templates, code, features, and branding, is our intellectual property protected by South African and international copyright laws. You may not:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                  <li>Copy, reproduce, or distribute our templates or designs;</li>
                  <li>Reverse engineer or attempt to extract the source code;</li>
                  <li>Create derivative works based on our platform;</li>
                  <li>Use our branding or trademarks without permission.</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-slate-900 mb-4">8. Disclaimer of Warranties</h2>
                <div className="bg-red-50 border border-red-200 rounded-xl p-5 mb-4">
                  <h3 className="text-lg font-semibold text-red-800 mb-2">IMPORTANT - PLEASE READ CAREFULLY</h3>
                  <p className="text-red-700 font-medium">
                    THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS. YOU USE CAREER STEP ENTIRELY AT YOUR OWN RISK.
                  </p>
                </div>
                <p className="text-slate-600 mb-4">
                  To the maximum extent permitted by South African law, Career Step expressly disclaims all warranties of any kind, whether express, implied, statutory, or otherwise, including but not limited to:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                  <li>Any implied warranties of merchantability, fitness for a particular purpose, title, and non-infringement;</li>
                  <li>Any warranty that the Service will be uninterrupted, timely, secure, or error-free;</li>
                  <li>Any warranty regarding the accuracy, reliability, or completeness of any content, data, or information;</li>
                  <li>Any warranty that the Service will meet your requirements or expectations;</li>
                  <li>Any warranty regarding the results that may be obtained from the use of the Service;</li>
                  <li>Any warranty that defects will be corrected;</li>
                  <li>Any warranty regarding AI-generated content, suggestions, or recommendations;</li>
                  <li>Any warranty regarding salary data, job market information, or career advice;</li>
                  <li>Any warranty regarding third-party job listings or external content;</li>
                  <li>Any warranty regarding ATS compatibility scores or job matching results;</li>
                  <li>Any warranty regarding employment outcomes, interviews, or job offers.</li>
                </ul>
                <p className="text-slate-600 mt-4 font-medium">
                  You acknowledge and agree that Career Step has no control over, and no duty to take any action regarding employment decisions made by any employer or recruiter. 
                  Any reliance on the Service is strictly at your own risk.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-slate-900 mb-4">9. Limitation of Liability</h2>
                <div className="bg-red-50 border border-red-200 rounded-xl p-5 mb-4">
                  <h3 className="text-lg font-semibold text-red-800 mb-2">LIMITATION OF LIABILITY - PLEASE READ</h3>
                  <p className="text-red-700">
                    TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL CAREER STEP, ITS DIRECTORS, EMPLOYEES, PARTNERS, AGENTS, SUPPLIERS, OR AFFILIATES BE LIABLE FOR ANY DAMAGES WHATSOEVER.
                  </p>
                </div>
                <p className="text-slate-600 mb-4">
                  To the fullest extent permitted by South African law, Career Step shall not be liable for:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                  <li>Any direct, indirect, incidental, special, consequential, punitive, or exemplary damages;</li>
                  <li>Any loss of profits, revenue, business, savings, goodwill, or anticipated savings;</li>
                  <li>Any loss of data or corruption of data;</li>
                  <li>Any loss of employment opportunities, job offers, or interviews;</li>
                  <li>Any loss of income or earning potential;</li>
                  <li>Any reputational damage or harm to your professional standing;</li>
                  <li>Any decisions made by employers, recruiters, or hiring managers;</li>
                  <li>Any reliance on CV content, AI suggestions, or any information provided through the Service;</li>
                  <li>Any errors, inaccuracies, or omissions in AI-generated content;</li>
                  <li>Any inaccuracies in salary data, market information, or career guidance;</li>
                  <li>Any third-party actions, content, products, or services;</li>
                  <li>Any unauthorised access to or alteration of your data;</li>
                  <li>Any interruption or cessation of the Service;</li>
                  <li>Any bugs, viruses, or other harmful components;</li>
                  <li>Any other matter relating to the Service.</li>
                </ul>
                <p className="text-slate-600 mt-4">
                  These limitations apply regardless of the legal theory (contract, tort, negligence, strict liability, warranty, or otherwise), 
                  even if Career Step has been advised of the possibility of such damages, and even if a remedy fails of its essential purpose.
                </p>
                <p className="text-slate-600 mt-4 font-semibold">
                  IN NO EVENT SHALL OUR TOTAL LIABILITY TO YOU FOR ALL CLAIMS EXCEED THE LESSER OF: (A) THE AMOUNT YOU PAID TO US IN THE 
                  THREE (3) MONTHS PRECEDING THE CLAIM, OR (B) ONE HUNDRED SOUTH AFRICAN RAND (R100.00).
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-slate-900 mb-4">10. Assumption of Risk</h2>
                <p className="text-slate-600 mb-4">
                  By using Career Step, you expressly acknowledge and agree that:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                  <li>You use the Service entirely at your own risk;</li>
                  <li>You are solely responsible for the accuracy and truthfulness of all content in your CV;</li>
                  <li>You are solely responsible for reviewing and verifying any AI-generated content before use;</li>
                  <li>You assume full responsibility for any decisions you make based on information from the Service;</li>
                  <li>You assume full responsibility for your job search, applications, and career decisions;</li>
                  <li>Career Step is a tool to assist you, not a guarantee of any outcome;</li>
                  <li>Success in job applications depends on many factors outside our control;</li>
                  <li>We have no control over how employers or recruiters evaluate your CV or application.</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-slate-900 mb-4">11. Indemnification</h2>
                <p className="text-slate-600 mb-4">
                  You agree to fully indemnify, defend, and hold harmless Career Step, its parent company, subsidiaries, affiliates, 
                  officers, directors, employees, agents, partners, licensors, and suppliers from and against any and all claims, 
                  demands, actions, suits, proceedings, liabilities, damages, losses, costs, and expenses (including reasonable 
                  attorneys' fees and court costs) arising from or related to:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                  <li>Your use or misuse of the Service;</li>
                  <li>Your violation of these Terms;</li>
                  <li>Your violation of any applicable law, regulation, or third-party right;</li>
                  <li>Any content you create, submit, or share through the Service;</li>
                  <li>Any false, misleading, inaccurate, or fraudulent information in your CV or account;</li>
                  <li>Any misrepresentation of your qualifications, experience, skills, or credentials;</li>
                  <li>Any claim by an employer, recruiter, or any third party related to your CV or job applications;</li>
                  <li>Your failure to review and verify AI-generated content;</li>
                  <li>Any reliance on information, content, or features provided by the Service;</li>
                  <li>Any claim that your CV content infringes the rights of any third party;</li>
                  <li>Any unauthorised use of your account;</li>
                  <li>Any dispute between you and any employer or third party.</li>
                </ul>
                <p className="text-slate-600 mt-4">
                  This indemnification obligation will survive the termination of these Terms and your use of the Service.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-slate-900 mb-4">12. No Employment Guarantee</h2>
                <div className="bg-red-50 border border-red-200 rounded-xl p-5 mb-4">
                  <h3 className="text-lg font-semibold text-red-800 mb-2">CRITICAL DISCLAIMER</h3>
                  <p className="text-red-700">
                    CAREER STEP IS NOT A RECRUITMENT AGENCY, EMPLOYMENT SERVICE, OR JOB PLACEMENT COMPANY. 
                    WE DO NOT GUARANTEE EMPLOYMENT, INTERVIEWS, JOB OFFERS, OR ANY SPECIFIC CAREER OUTCOMES. 
                    WE ARE SOLELY A CV CREATION AND JOB READINESS TOOL.
                  </p>
                </div>
                <p className="text-slate-600 mb-4">
                  You expressly acknowledge and agree that:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                  <li>Career Step does not guarantee that you will obtain employment, interviews, or job offers;</li>
                  <li>Career Step does not guarantee that your CV will be selected by any employer or pass any ATS system;</li>
                  <li>Career Step does not guarantee the accuracy of ATS scores or job matching percentages;</li>
                  <li>Career Step has no relationship with any employer and cannot influence hiring decisions;</li>
                  <li>Success depends on your qualifications, the job market, and many factors outside our control;</li>
                  <li>A well-crafted CV is only one factor in job search success;</li>
                  <li>You are solely responsible for your job search strategy and career decisions.</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-slate-900 mb-4">13. Third-Party Content and Links</h2>
                <p className="text-slate-600 mb-4">
                  Our Service may include links to third-party websites, job listings, or resources. Career Step disclaims all responsibility and liability for:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                  <li>The content, accuracy, opinions, reliability, or availability of third-party sites;</li>
                  <li>Job listings aggregated or linked from external job boards;</li>
                  <li>Any transactions, interactions, or disputes between you and third parties;</li>
                  <li>The privacy practices, security, or policies of third-party websites;</li>
                  <li>Any products, services, or content provided by third parties;</li>
                  <li>Any damages or losses caused by your reliance on third-party content.</li>
                </ul>
                <p className="text-slate-600 mt-3 font-medium">
                  Your use of third-party sites is entirely at your own risk and subject to their terms and conditions. 
                  We encourage you to review the terms and privacy policies of any third-party sites you visit.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-slate-900 mb-4">14. Release</h2>
                <p className="text-slate-600 mb-4">
                  You hereby release and forever discharge Career Step, its affiliates, officers, directors, employees, agents, 
                  and successors from any and all claims, demands, damages, losses, costs, expenses, and liabilities of every 
                  kind and nature, known and unknown, arising out of or in any way connected with:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                  <li>Any dispute between you and any employer, recruiter, or third party;</li>
                  <li>The acts or omissions of any employer, recruiter, or third party;</li>
                  <li>Your job search, applications, interviews, or employment outcomes;</li>
                  <li>Any content you create using the Service;</li>
                  <li>Any reliance on information provided through the Service.</li>
                </ul>
                <p className="text-slate-600 mt-4">
                  You expressly waive any protection that would limit the scope of this release to claims known or suspected 
                  to exist at the time of signing this agreement.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-slate-900 mb-4">15. Termination</h2>
                <h3 className="text-lg font-semibold text-slate-800 mb-2">15.1 Termination by You</h3>
                <p className="text-slate-600 mb-4">
                  You may terminate your account at any time by cancelling your subscription and requesting account deletion through our support channels.
                </p>
                
                <h3 className="text-lg font-semibold text-slate-800 mb-2">15.2 Termination by Us</h3>
                <p className="text-slate-600 mb-4">
                  We may suspend or terminate your account immediately, without notice or liability, if you:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                  <li>Violate these Terms of Service;</li>
                  <li>Provide false or misleading information;</li>
                  <li>Engage in fraudulent activity or payment fraud;</li>
                  <li>Abuse the Service or other users;</li>
                  <li>Attempt to circumvent subscription limits or security measures;</li>
                  <li>Use the Service for any unlawful purpose;</li>
                  <li>Create CVs with intentionally false qualifications or experience.</li>
                </ul>
                
                <h3 className="text-lg font-semibold text-slate-800 mb-2 mt-4">15.3 Effect of Termination</h3>
                <p className="text-slate-600">
                  Upon termination, your right to use the Service ceases immediately. We may delete your account and CVs without liability. 
                  Provisions regarding intellectual property, disclaimers, limitation of liability, indemnification, release, and dispute resolution survive termination indefinitely.
                  No refunds will be provided for termination due to your breach of these Terms.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-slate-900 mb-4">16. Force Majeure</h2>
                <p className="text-slate-600">
                  Career Step shall not be liable for any failure or delay in performing our obligations where such failure or delay results from circumstances beyond our reasonable control, including but not limited to: 
                  acts of God, natural disasters, war, terrorism, riots, embargoes, acts of civil or military authorities, fire, floods, 
                  accidents, strikes, power outages, internet or telecommunications failures, pandemics, government actions, or third-party service provider failures.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-slate-900 mb-4">17. Modifications to Terms</h2>
                <p className="text-slate-600 mb-4">
                  We reserve the right to modify these Terms at any time in our sole discretion. When we make changes:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                  <li>We will update the "Effective Date" at the top of this page;</li>
                  <li>For material changes, we will notify you via email or platform notification;</li>
                  <li>Your continued use of the Service after changes constitutes acceptance;</li>
                  <li>If you do not agree to modified terms, you must stop using the Service and cancel your subscription immediately.</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-slate-900 mb-4">18. Governing Law and Disputes</h2>
                <p className="text-slate-600 mb-4">
                  These Terms are governed by and construed in accordance with the laws of the Republic of South Africa, 
                  without regard to its conflict of law provisions.
                </p>
                
                <h3 className="text-lg font-semibold text-slate-800 mb-2">18.1 Informal Resolution</h3>
                <p className="text-slate-600 mb-4">
                  Before initiating any formal dispute resolution proceedings, you agree to first contact us at support@careerstep.co.za 
                  and attempt to resolve the dispute informally for at least 30 days.
                </p>
                
                <h3 className="text-lg font-semibold text-slate-800 mb-2">18.2 Binding Arbitration</h3>
                <p className="text-slate-600 mb-4">
                  If informal resolution fails, any dispute, controversy, or claim arising from or relating to these Terms, 
                  or the breach, termination, or validity thereof, shall be finally resolved through binding arbitration 
                  administered by the Arbitration Foundation of Southern Africa (AFSA) in accordance with its rules. 
                  The arbitration shall take place in Johannesburg, South Africa, and be conducted in English. 
                  The arbitrator's decision shall be final and binding, and judgment on the award may be entered in any court of competent jurisdiction.
                </p>
                
                <h3 className="text-lg font-semibold text-slate-800 mb-2">18.3 Class Action Waiver</h3>
                <p className="text-slate-600 mb-4">
                  YOU AGREE THAT ANY DISPUTE RESOLUTION PROCEEDINGS WILL BE CONDUCTED ONLY ON AN INDIVIDUAL BASIS AND NOT IN A CLASS, 
                  CONSOLIDATED, OR REPRESENTATIVE ACTION. YOU HEREBY WAIVE ANY RIGHT TO PARTICIPATE IN A CLASS ACTION LAWSUIT OR 
                  CLASS-WIDE ARBITRATION AGAINST CAREER STEP.
                </p>
                
                <h3 className="text-lg font-semibold text-slate-800 mb-2">18.4 Limitation on Time to File Claims</h3>
                <p className="text-slate-600 mb-4">
                  ANY CAUSE OF ACTION OR CLAIM YOU MAY HAVE ARISING OUT OF OR RELATING TO THESE TERMS OR THE SERVICE MUST BE 
                  COMMENCED WITHIN ONE (1) YEAR AFTER THE CAUSE OF ACTION ACCRUES; OTHERWISE, SUCH CAUSE OF ACTION OR CLAIM IS 
                  PERMANENTLY BARRED.
                </p>
                
                <h3 className="text-lg font-semibold text-slate-800 mb-2">18.5 Small Claims Exception</h3>
                <p className="text-slate-600">
                  Notwithstanding the above, either party may bring an individual action in small claims court for disputes within the court's jurisdiction.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-slate-900 mb-4">19. Consumer Protection</h2>
                <p className="text-slate-600">
                  These Terms are intended to comply with applicable South African law. To the extent that the Consumer Protection Act 68 of 2008 (CPA) 
                  or other mandatory consumer protection legislation applies to you and these Terms, any provisions that would otherwise be 
                  prohibited or unenforceable shall be modified to the minimum extent necessary to comply. Where these Terms conflict with 
                  mandatory consumer protection provisions that cannot be waived, those provisions shall prevail, but only to the extent required by law.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-slate-900 mb-4">20. Severability</h2>
                <p className="text-slate-600">
                  If any provision of these Terms is found to be invalid, illegal, or unenforceable by a court of competent jurisdiction, 
                  the remaining provisions shall continue in full force and effect. The invalid provision shall be modified to the minimum 
                  extent necessary to make it valid and enforceable while preserving the parties' original intent. If modification is not 
                  possible, the invalid provision shall be severed, and the remaining Terms shall be enforced.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-slate-900 mb-4">21. Waiver</h2>
                <p className="text-slate-600">
                  Our failure to enforce any right or provision of these Terms shall not constitute a waiver of such right or provision. 
                  Any waiver of any provision of these Terms will be effective only if in writing and signed by an authorised representative of Career Step.
                  No waiver of any term shall be deemed a further or continuing waiver of such term or any other term.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-slate-900 mb-4">22. Entire Agreement</h2>
                <p className="text-slate-600">
                  These Terms, together with our Privacy Policy and any other legal notices or policies published by us on the Service, 
                  constitute the entire agreement between you and Career Step regarding your use of the Service. 
                  These Terms supersede all prior agreements, communications, representations, and understandings, whether written or oral, 
                  relating to the subject matter hereof. Any terms or conditions provided by you (including in any purchase order) 
                  that are different from or in addition to these Terms are expressly rejected.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-slate-900 mb-4">23. Assignment</h2>
                <p className="text-slate-600">
                  You may not assign or transfer these Terms, or any rights or obligations hereunder, without our prior written consent. 
                  We may assign or transfer these Terms, in whole or in part, without restriction. Subject to the foregoing, these Terms 
                  will bind and inure to the benefit of the parties and their respective successors and permitted assigns.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-slate-900 mb-4">24. Acknowledgement</h2>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
                  <p className="text-amber-800">
                    BY USING CAREER STEP, YOU ACKNOWLEDGE THAT YOU HAVE READ THESE TERMS OF SERVICE, UNDERSTAND THEM, AND AGREE TO BE BOUND BY THEM. 
                    YOU FURTHER ACKNOWLEDGE THAT THESE TERMS, TOGETHER WITH THE PRIVACY POLICY, REPRESENT THE COMPLETE AND EXCLUSIVE STATEMENT 
                    OF THE AGREEMENT BETWEEN YOU AND CAREER STEP AND SUPERSEDE ANY PRIOR AGREEMENT OR REPRESENTATION.
                  </p>
                </div>
              </section>

              <section>
                <h2 className="text-xl font-bold text-slate-900 mb-4">25. Contact Information</h2>
                <p className="text-slate-600 mb-4">
                  For questions, concerns, or complaints about these Terms of Service, please contact us:
                </p>
                <div className="bg-slate-50 rounded-xl p-5">
                  <p className="text-slate-700 font-semibold text-lg mb-2">Career Step</p>
                  <p className="text-slate-600 mb-1"><strong>General Enquiries:</strong> support@careerstep.co.za</p>
                  <p className="text-slate-600 mb-1"><strong>Legal Enquiries:</strong> support@careerstep.co.za</p>
                  <p className="text-slate-600"><strong>Website:</strong> www.careerstep.co.za</p>
                </div>
              </section>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TermsPage;
